import React, { useReducer, useCallback, useEffect, useMemo } from 'react';
import { gql } from '@apollo/client';
import { Button } from '@moda/om';
import classNames from 'classnames';
import { SwiperSlide } from 'swiper/react';
import { useParams, Link } from 'react-router-dom';
import { compact, formatPrice } from '@moda/portal-stanchions';
import { ResponsiveImage } from '../../../../components/ResponsiveImage';
import { Image } from '../../../../components/Image';
import { hrefFor } from '../../../../routers';
import {
  MobileLookviewDetailViewFragment,
  MobileLookviewDetailViewVariantFragment
} from '../../../../generated/types';
import { VerticalSnapper } from '../../../../components/VerticalSnapper';
import { Swiper } from '../../../../components/Swiper';
import {
  MobileLookviewPopupSizeSelector,
  MOBILE_LOOKVIEW_POPUP_SIZE_SELECTOR_FRAGMENT
} from '../MobileLookviewPopupSizeSelector';
import {
  MOBILE_LOOKVIEW_VARIANT_IMAGE_VIEW_FRAGMENT,
  MobileLookviewVariantImageView
} from '../MobileLookviewVariantImageView';

import './MobileLookviewDetailView.scss';

export const MOBILE_LOOKVIEW_DETAIL_VIEW_VARIANT_FRAGMENT = gql`
  fragment MobileLookviewDetailViewVariantFragment on Variant {
    id
    name
    available
    designerSlug: designer_slug
    masterVariant: master_variants_data {
      id
      href
      prices(country_code: $countryCode) {
        currentPrice: current_price {
          currency
          price
        }
      }
      ...MobileLookviewVariantImageViewFragment
    }
    otherVariants: other_variants_data {
      id
    }
    ...MobileLookviewPopupSizeSelectorFragment
  }
  ${MOBILE_LOOKVIEW_POPUP_SIZE_SELECTOR_FRAGMENT}
  ${MOBILE_LOOKVIEW_VARIANT_IMAGE_VIEW_FRAGMENT}
`;

export const MOBILE_LOOKVIEW_DETAIL_VIEW_FRAGMENT = gql`
  fragment MobileLookviewDetailViewFragment on Look {
    id
    name
    trunkshowName: trunkshow_name
    primaryImageUrls: primary_image_urls {
      main
    }
    variants {
      edges {
        node {
          ...MobileLookviewDetailViewVariantFragment
        }
      }
    }
  }
  ${MOBILE_LOOKVIEW_DETAIL_VIEW_VARIANT_FRAGMENT}
`;

interface Props {
  look: MobileLookviewDetailViewFragment;
  lookImages: string[];
  activeLookIndex: number;
  onSetActiveLookIndex(index: number): void;
}

interface State {
  activeVariant: MobileLookviewDetailViewVariantFragment;
  currentStep: number;
  showSizeSelector: boolean;
}

type Action =
  | {
      type: 'SET_VARIANT';
      payload: { variant: MobileLookviewDetailViewVariantFragment; stepNumber: number };
    }
  | {
      type: 'SHOW_SIZE_SELECTOR';
      payload: { showSizeSelector: boolean };
    };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    // TODO: Just set a cursor and/or activeId?
    case 'SET_VARIANT':
      return {
        ...state,
        activeVariant: action.payload.variant,
        currentStep: action.payload.stepNumber
      };
    case 'SHOW_SIZE_SELECTOR':
      return {
        ...state,
        showSizeSelector: action.payload.showSizeSelector
      };
  }
};

// TODO: All this business with currentStep is overcomplicated
// and could be drastically simplified
export const MobileLookviewDetailView: React.FC<Props> = ({
  look,
  lookImages,
  activeLookIndex,
  onSetActiveLookIndex
}) => {
  const { vertical = 'women' } = useParams<{ vertical?: string }>();

  const variants = useMemo(
    () => compact(compact(look.variants?.edges).map(({ node }) => node)),
    [look.variants]
  );

  const [{ activeVariant, currentStep, showSizeSelector }, dispatch] = useReducer(reducer, {
    activeVariant: variants[0],
    currentStep: 0,
    showSizeSelector: false
  });

  const handleEnter = useCallback(
    (stepNumber: number) => {
      dispatch({
        type: 'SET_VARIANT',
        payload: { variant: variants[stepNumber - 1] || variants[0], stepNumber }
      });
    },
    [variants]
  );

  const handleCloseSizeSelector = useCallback(
    () => dispatch({ type: 'SHOW_SIZE_SELECTOR', payload: { showSizeSelector: false } }),
    []
  );

  const handleOpenSizeSelector = useCallback(
    () => dispatch({ type: 'SHOW_SIZE_SELECTOR', payload: { showSizeSelector: true } }),
    []
  );

  useEffect(
    () => dispatch({ type: 'SET_VARIANT', payload: { variant: variants[0], stepNumber: 0 } }),
    [variants]
  );

  const [, , , , productSlug] = activeVariant.masterVariant.href.split('/');

  return (
    <>
      <div className="MobileLookviewDetailView">
        <div className="MobileLookviewDetailView__viewer">
          <div className="MobileLookviewDetailView__snapper">
            <VerticalSnapper key={look.id} onEnter={handleEnter}>
              <ResponsiveImage
                className="MobileLookviewDetailView__image"
                maxWidth={775}
                maxHeight={1250}
                src={look.primaryImageUrls?.main || '//:0'}
                alt={look.trunkshowName || 'Look'}
              />

              {variants.map(variant => (
                <MobileLookviewVariantImageView
                  key={variant.id}
                  variant={variant.masterVariant}
                  alt={variant.name || look.trunkshowName || 'Look'}
                />
              ))}
            </VerticalSnapper>
          </div>
        </div>

        <div className="MobileLookviewDetailView__carousel">
          <Swiper
            slidesPerView={10}
            initialSlide={activeLookIndex}
            centeredSlides
            slideToClickedSlide
            spaceBetween={3}
            className="MobileLookviewDetailView__rail"
            onSlideChange={swiper => onSetActiveLookIndex(swiper.activeIndex)}
          >
            {lookImages.map((src, index) => (
              <SwiperSlide
                key={`${src}-${index}`}
                className={classNames('MobileLookviewDetailView__slide', {
                  'MobileLookviewDetailView__slide--active': index === activeLookIndex
                })}
              >
                <Image
                  src={src}
                  alt={look.name || 'Look'}
                  className="MobileLookviewDetailView__slide-image"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="MobileLookviewDetailView__actions">
          <h1 className="MobileLookviewDetailView__name">
            {currentStep === 0 ? look.trunkshowName : activeVariant.name || look.trunkshowName}
          </h1>

          <div className="MobileLookviewDetailView__details">
            {currentStep === 0 && (
              <>
                {variants.length} Item{variants.length === 1 ? '' : 's'}
              </>
            )}

            {currentStep > 0 && (
              <>
                <div>
                  {activeVariant.masterVariant.prices?.currentPrice
                    ? formatPrice(
                        activeVariant.masterVariant.prices.currentPrice.currency,
                        activeVariant.masterVariant.prices.currentPrice.price
                      )
                    : ''}
                </div>

                <Link
                  to={hrefFor.ProductDetailPage({
                    vertical,
                    designerSlug: activeVariant.designerSlug || '',
                    productSlug,
                    variantId: activeVariant.id
                  })}
                >
                  View Full Details
                </Link>
              </>
            )}
          </div>

          <Button
            className="MobileLookviewDetailView__button"
            disabled={currentStep === 0}
            onClick={handleOpenSizeSelector}
          >
            Select Size
          </Button>
        </div>
      </div>

      <MobileLookviewPopupSizeSelector
        show={showSizeSelector}
        variant={activeVariant}
        onClose={handleCloseSizeSelector}
      />
    </>
  );
};
